/**
 * AutoMLWizard — 5-step guided AutoML sweep launch.
 * Phase 4.10 β2 — WQ-237.
 *
 * Steps:
 *   1. Dataset picker — DataCatalog entries + composed datasets (WQ-236)
 *   2. Target column — schema-driven dropdown
 *   3. Problem type — auto-inferred via heuristic on /automl/profile; user can override
 *   4. Constraints — time budget, primary metric, validation split
 *   5. Review & Launch
 *
 * Local state machine; no React Router dependency (β11/WQ-246 will deep-link
 * later). Closes on submit or cancel; parent dashboard's polling picks up
 * the new sweep via fetchExperiments().
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  startSweep,
  getDatasetSchema,
  inferProblemType,
  listAvailableDatasets,
  type SweepConfig,
  type DatasetOption,
  type ProblemType,
} from '../../services/automlService';
import WizardModalShell from '../wizard/WizardModalShell';

type WizardStep = 1 | 2 | 3 | 4 | 5;

interface AutoMLWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onLaunched: (sweepId: string) => void;
}

const STEP_TITLES: Record<WizardStep, string> = {
  1: 'Choose a dataset',
  2: 'Choose a target column',
  3: 'Confirm problem type',
  4: 'Set constraints',
  5: 'Review & launch',
};

const METRIC_OPTIONS: Record<ProblemType, string[]> = {
  classification: ['auto', 'accuracy', 'f1', 'roc_auc'],
  regression: ['auto', 'rmse', 'mae', 'r2'],
  clustering: ['auto', 'silhouette'],
};

const AutoMLWizard: React.FC<AutoMLWizardProps> = ({ isOpen, onClose, onLaunched }) => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 state
  const [datasets, setDatasets] = useState<DatasetOption[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [datasetsError, setDatasetsError] = useState<string | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<DatasetOption | null>(null);

  // Step 2 state
  const [columns, setColumns] = useState<{ name: string; dtype: string }[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState<string | null>(null);
  const [targetColumn, setTargetColumn] = useState<string>('');

  // Step 3 state
  const [inferredProblemType, setInferredProblemType] = useState<ProblemType | null>(null);
  const [inferenceRationale, setInferenceRationale] = useState<string | null>(null);
  const [problemType, setProblemType] = useState<ProblemType>('classification');

  // Step 4 state
  const [timeBudgetMinutes, setTimeBudgetMinutes] = useState<number>(15);
  const [primaryMetric, setPrimaryMetric] = useState<string>('auto');
  const [validationSplit, setValidationSplit] = useState<number>(0.2);

  // Submit state
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Reset wizard each time it opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDataset(null);
      setColumns([]);
      setColumnsError(null);
      setTargetColumn('');
      setInferredProblemType(null);
      setInferenceRationale(null);
      setProblemType('classification');
      setTimeBudgetMinutes(15);
      setPrimaryMetric('auto');
      setValidationSplit(0.2);
      setLaunchError(null);
    }
  }, [isOpen]);

  // Load datasets when wizard opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setDatasetsLoading(true);
      setDatasetsError(null);
      try {
        const data = await listAvailableDatasets(authFetch);
        if (cancelled) return;
        setDatasets([...(data.catalog ?? []), ...(data.composed ?? [])]);
      } catch (err) {
        if (!cancelled) setDatasetsError(err instanceof Error ? err.message : 'Failed to load datasets');
      } finally {
        if (!cancelled) setDatasetsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, authFetch]);

  // Load schema when a dataset is picked
  useEffect(() => {
    if (!selectedDataset) {
      setColumns([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setColumnsLoading(true);
      setColumnsError(null);
      try {
        const schema = await getDatasetSchema(authFetch, selectedDataset.id, selectedDataset.source);
        if (!cancelled) setColumns(schema.columns ?? []);
      } catch (err) {
        if (!cancelled) {
          setColumnsError(err instanceof Error ? err.message : 'Failed to load schema');
          setColumns([]);
        }
      } finally {
        if (!cancelled) setColumnsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDataset, authFetch]);

  // Infer problem type when target column is chosen
  useEffect(() => {
    if (!selectedDataset || !targetColumn) {
      setInferredProblemType(null);
      setInferenceRationale(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await inferProblemType(authFetch, selectedDataset.id, targetColumn);
        if (cancelled) return;
        if (result) {
          setInferredProblemType(result.problem_type);
          setInferenceRationale(result.rationale);
          setProblemType(result.problem_type);
        } else {
          setInferredProblemType(null);
          setInferenceRationale(null);
          // leave problemType at its current value (default 'classification')
        }
      } catch {
        if (!cancelled) {
          setInferredProblemType(null);
          setInferenceRationale(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDataset, targetColumn, authFetch]);

  // Adjust primary metric when problem type changes
  useEffect(() => {
    const valid = METRIC_OPTIONS[problemType];
    if (!valid.includes(primaryMetric)) {
      setPrimaryMetric('auto');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemType]);

  const canAdvance = useMemo(() => {
    if (step === 1) return !!selectedDataset;
    if (step === 2) return !!targetColumn && columns.length > 0;
    if (step === 3) return !!problemType;
    if (step === 4) return timeBudgetMinutes >= 1 && validationSplit >= 0.05 && validationSplit <= 0.5;
    return false;
  }, [step, selectedDataset, targetColumn, columns.length, problemType, timeBudgetMinutes, validationSplit]);

  const handleNext = () => {
    if (!canAdvance) return;
    setStep((prev) => (prev < 5 ? ((prev + 1) as WizardStep) : prev));
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? ((prev - 1) as WizardStep) : prev));
  };

  const handleLaunch = async () => {
    if (!selectedDataset || !targetColumn) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const config: SweepConfig = {
        dataset_id: selectedDataset.id,
        target_column: targetColumn,
        problem_type: problemType,
        time_budget_seconds: timeBudgetMinutes * 60,
        metric: primaryMetric === 'auto' ? undefined : primaryMetric,
      };
      const status = await startSweep(authFetch, config);
      onLaunched(status.sweep_id);
      onClose();
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Failed to launch sweep');
    } finally {
      setLaunching(false);
    }
  };

  const catalogDatasets = datasets.filter((d) => d.source === 'catalog');
  const composedDatasets = datasets.filter((d) => d.source === 'composed');

  return (
    <WizardModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Launch AutoML Sweep"
      totalSteps={5}
      currentStep={step}
      stepTitle={STEP_TITLES[step]}
      testIdPrefix="automl-wizard"
      canAdvance={canAdvance}
      submitting={launching}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleLaunch}
      launchError={launchError}
    >
      <>
          {step === 1 && (
            <div data-testid="automl-wizard-step-1">
              {datasetsLoading && (
                <div data-testid="automl-wizard-datasets-loading" style={{ color: 'var(--text-secondary)' }}>
                  Loading datasets...
                </div>
              )}
              {datasetsError && (
                <div data-testid="automl-wizard-datasets-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 12,
                }}>
                  {datasetsError}
                </div>
              )}
              {!datasetsLoading && !datasetsError && datasets.length === 0 && (
                <div data-testid="automl-wizard-empty-datasets" style={{
                  padding: 20, textAlign: 'center', color: 'var(--text-secondary)',
                  border: '1px dashed var(--border)', borderRadius: 8,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>No datasets available</div>
                  <div style={{ fontSize: 13 }}>
                    Register one in Data Sources or compose one in the Datasets tab.
                  </div>
                </div>
              )}
              {catalogDatasets.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    From Data Catalog
                  </div>
                  <div data-testid="automl-wizard-catalog-list">
                    {catalogDatasets.map((d) => (
                      <button
                        key={d.id}
                        data-testid={`automl-wizard-dataset-${d.id}`}
                        onClick={() => setSelectedDataset(d)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                          border: selectedDataset?.id === d.id ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                          background: selectedDataset?.id === d.id ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                          color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{d.name}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>(catalog)</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {composedDatasets.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    Composed Datasets
                  </div>
                  <div data-testid="automl-wizard-composed-list">
                    {composedDatasets.map((d) => (
                      <button
                        key={d.id}
                        data-testid={`automl-wizard-dataset-${d.id}`}
                        onClick={() => setSelectedDataset(d)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                          border: selectedDataset?.id === d.id ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                          background: selectedDataset?.id === d.id ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                          color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{d.name}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>
                          (composed{d.row_count !== null ? `, ${d.row_count} rows` : ''})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="automl-wizard-step-2">
              {columnsLoading && (
                <div data-testid="automl-wizard-columns-loading" style={{ color: 'var(--text-secondary)' }}>
                  Loading schema...
                </div>
              )}
              {columnsError && (
                <div data-testid="automl-wizard-columns-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8,
                }}>
                  {columnsError}
                </div>
              )}
              {!columnsLoading && !columnsError && columns.length === 0 && (
                <div data-testid="automl-wizard-no-columns" style={{
                  padding: 20, textAlign: 'center', color: 'var(--text-secondary)',
                }}>
                  No columns found in this dataset.
                </div>
              )}
              {columns.length > 0 && (
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                    Target column *
                  </label>
                  <select
                    data-testid="automl-wizard-target-select"
                    value={targetColumn}
                    onChange={(e) => setTargetColumn(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 6,
                      border: '1px solid var(--border)', background: 'var(--bg-primary)',
                      color: 'var(--text-primary)', fontSize: 13,
                    }}
                  >
                    <option value="">Select a target column...</option>
                    {columns.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name} ({c.dtype})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="automl-wizard-step-3">
              {inferenceRationale && (
                <div data-testid="automl-wizard-inference-rationale" style={{
                  padding: 12, background: 'rgba(59,130,246,0.05)',
                  border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8,
                  fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12,
                }}>
                  {inferenceRationale}
                </div>
              )}
              <div role="radiogroup" data-testid="automl-wizard-problem-type-group">
                {(['classification', 'regression', 'clustering'] as ProblemType[]).map((pt) => (
                  <label
                    key={pt}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      border: problemType === pt ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                      background: problemType === pt ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                    }}
                  >
                    <input
                      type="radio"
                      name="automl-wizard-problem-type"
                      data-testid={`automl-wizard-problem-type-${pt}`}
                      value={pt}
                      checked={problemType === pt}
                      onChange={() => setProblemType(pt)}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{pt}</span>
                    {inferredProblemType === pt && (
                      <span style={{ fontSize: 11, color: 'var(--accent-primary)', marginLeft: 'auto' }}>
                        suggested
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="automl-wizard-step-4">
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Time budget (minutes) *
                </label>
                <input
                  type="number"
                  data-testid="automl-wizard-time-budget"
                  min={1}
                  max={1440}
                  value={timeBudgetMinutes}
                  onChange={(e) => setTimeBudgetMinutes(parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
                {timeBudgetMinutes < 1 && (
                  <div data-testid="automl-wizard-time-budget-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    Time budget must be at least 1 minute.
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Primary metric
                </label>
                <select
                  data-testid="automl-wizard-primary-metric"
                  value={primaryMetric}
                  onChange={(e) => setPrimaryMetric(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                >
                  {METRIC_OPTIONS[problemType].map((m) => (
                    <option key={m} value={m}>{m === 'auto' ? 'auto (server picks)' : m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Validation split: {Math.round(validationSplit * 100)}%
                </label>
                <input
                  type="number"
                  step="0.05"
                  min={0.05}
                  max={0.5}
                  data-testid="automl-wizard-validation-split"
                  value={validationSplit}
                  onChange={(e) => setValidationSplit(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
                {(validationSplit < 0.05 || validationSplit > 0.5) && (
                  <div data-testid="automl-wizard-validation-split-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    Validation split must be between 5% and 50%.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 5 && selectedDataset && (
            <div data-testid="automl-wizard-step-5">
              <div style={{
                padding: 16, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.6,
              }}>
                <div data-testid="automl-wizard-review-summary">
                  Run AutoML on dataset <strong>{selectedDataset.name}</strong> ({selectedDataset.source}
                  {selectedDataset.row_count !== null ? `, ${selectedDataset.row_count} rows` : ''}).
                  <br />
                  Target column: <code>{targetColumn}</code>.
                  <br />
                  Problem type: <strong>{problemType}</strong>.
                  <br />
                  Time budget: <strong>{timeBudgetMinutes} minute{timeBudgetMinutes === 1 ? '' : 's'}</strong>.
                  <br />
                  Primary metric: <strong>{primaryMetric}</strong>.
                  <br />
                  Validation split: <strong>{Math.round(validationSplit * 100)}%</strong>.
                </div>
              </div>
            </div>
          )}
      </>
    </WizardModalShell>
  );
};

export default AutoMLWizard;
